/**
 * Coup de cœur — wishlist en localStorage (clé mg_wishlist)
 */
class WishlistManager {
  constructor() {
    this.storageKey = 'mg_wishlist';
    this.init();
  }

  getWishlist() {
    try {
      const raw = localStorage.getItem(this.storageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  saveWishlist(list) {
    localStorage.setItem(this.storageKey, JSON.stringify(list));
  }

  normalizeProduct(product) {
    const id = String(product.id);
    return {
      id,
      handle: product.handle || '',
      title: product.title || '',
      image: product.image || '',
      price: Number(product.price) || 0,
      url: product.url || '',
      variantId: String(product.variantId || product.variant_id || ''),
    };
  }

  addToWishlist(product) {
    const p = this.normalizeProduct(product);
    if (!p.id || !p.variantId) return;
    const list = this.getWishlist().filter((item) => String(item.id) !== p.id);
    list.unshift(p);
    this.saveWishlist(list);
    this.updateAllHeartButtons();
    this.updateHeaderCount();
    if (this.isDrawerOpen()) this.renderDrawerItems();
  }

  removeFromWishlist(productId) {
    const id = String(productId);
    const list = this.getWishlist().filter((item) => String(item.id) !== id);
    this.saveWishlist(list);
    this.updateAllHeartButtons();
    this.updateHeaderCount();
    this.renderDrawerItems();
  }

  isInWishlist(productId) {
    const id = String(productId);
    return this.getWishlist().some((item) => String(item.id) === id);
  }

  toggleWishlistFromButton(btn) {
    const id = btn.dataset.productId;
    if (!id) return;
    if (this.isInWishlist(id)) {
      this.removeFromWishlist(id);
    } else {
      this.addToWishlist({
        id,
        handle: btn.dataset.productHandle,
        title: btn.dataset.productTitle,
        image: btn.dataset.productImage,
        price: btn.dataset.productPrice,
        url: btn.dataset.productUrl,
        variantId: btn.dataset.variantId,
      });
    }
    btn.classList.remove('animate');
    void btn.offsetWidth;
    btn.classList.add('animate');
  }

  updateAllHeartButtons() {
    document.querySelectorAll('.wishlist-btn').forEach((btn) => {
      const id = btn.dataset.productId;
      if (!id) return;
      btn.classList.toggle('active', this.isInWishlist(id));
    });
  }

  /** URL image principale du variant (JSON fiche produit Shopify). */
  variantImageFromJson(variant) {
    if (!variant || typeof variant !== 'object') return '';
    const fi = variant.featured_image;
    if (fi) {
      if (typeof fi === 'string') return fi;
      if (fi.src) return fi.src;
    }
    const fm = variant.featured_media;
    if (fm && typeof fm === 'object') {
      if (fm.preview_image && fm.preview_image.src) return fm.preview_image.src;
      if (fm.src) return fm.src;
    }
    return '';
  }

  bindSubscribeVariantChange() {
    if (this._variantChangeSubscribed) return;
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    this._variantChangeSubscribed = true;
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const data = event && event.data;
      if (!data || !data.variant || data.variant.id == null) return;
      const sectionId = data.sectionId != null ? String(data.sectionId) : '';
      const variant = data.variant;
      const imageUrl = this.variantImageFromJson(variant);
      document.querySelectorAll('.wishlist-btn--pdp').forEach((btn) => {
        if (sectionId && String(btn.dataset.sectionId || '') !== sectionId) return;
        btn.dataset.variantId = String(variant.id);
        if (variant.price != null) btn.dataset.productPrice = String(variant.price);
        if (imageUrl) btn.dataset.productImage = imageUrl;
      });
      this.updateAllHeartButtons();
    });
  }

  updateHeaderCount() {
    const el = document.getElementById('wishlist-count');
    if (!el) return;
    const n = this.getWishlist().length;
    const label = n > 99 ? '99+' : String(n);
    el.textContent = label;
    el.classList.toggle('visible', n > 0);
    if (n > 9 || label === '99+') {
      el.setAttribute('data-count-wide', 'true');
    } else {
      el.removeAttribute('data-count-wide');
    }
  }

  isDrawerOpen() {
    const drawer = document.getElementById('wishlist-drawer');
    return drawer && drawer.classList.contains('open');
  }

  openDrawer() {
    const drawer = document.getElementById('wishlist-drawer');
    const backdrop = document.getElementById('wishlist-backdrop');
    if (!drawer || !backdrop) return;
    drawer.classList.add('open');
    drawer.setAttribute('aria-hidden', 'false');
    backdrop.classList.add('open');
    document.body.style.overflow = 'hidden';
    this.renderDrawerItems();
  }

  closeDrawer() {
    const drawer = document.getElementById('wishlist-drawer');
    const backdrop = document.getElementById('wishlist-backdrop');
    if (!drawer || !backdrop) return;
    drawer.classList.remove('open');
    drawer.setAttribute('aria-hidden', 'true');
    backdrop.classList.remove('open');
    document.body.style.overflow = '';
  }

  formatMoney(cents) {
    const amount = (Number(cents) || 0) / 100;
    const code = window.shopCurrencyIso || 'EUR';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
        style: 'currency',
        currency: code,
      }).format(amount);
    } catch {
      return `${amount.toFixed(2)} ${code}`;
    }
  }

  escapeHtml(str) {
    const d = document.createElement('div');
    d.textContent = str == null ? '' : String(str);
    return d.innerHTML;
  }

  renderDrawerItems() {
    const container = document.getElementById('wishlist-items');
    const footer = document.getElementById('wishlist-drawer-footer');
    const addAllBtn = document.getElementById('wishlist-add-all-btn');
    if (!container) return;
    const items = this.getWishlist();
    const cfg = window.wishlistStrings || {};
    const continueUrl = cfg.continueUrl || '/collections/all';

    if (items.length === 0) {
      if (footer) footer.hidden = true;
      container.innerHTML = `
        <div class="wishlist-empty">
          <p>${this.escapeHtml(cfg.emptyText || 'Votre liste de coups de cœur est vide.')}</p>
          <a href="${this.escapeHtml(continueUrl)}">${this.escapeHtml(cfg.continueLabel || 'Continuer vos achats')}</a>
        </div>
      `;
      return;
    }

    if (footer) footer.hidden = false;
    if (addAllBtn && cfg.addAllToCart) addAllBtn.textContent = cfg.addAllToCart;

    const addLabel = cfg.addToCart || 'Ajouter au panier';
    const removeLabel = cfg.remove || 'Retirer';

    container.innerHTML = items
      .map((item) => {
        const title = this.escapeHtml(item.title);
        const url = this.escapeHtml(item.url);
        const img = this.escapeHtml(item.image);
        const vid = this.escapeHtml(item.variantId);
        const pid = this.escapeHtml(item.id);
        const price = this.formatMoney(item.price);
        return `
          <div class="wishlist-item" data-wishlist-product-id="${pid}">
            <a href="${url}" class="wishlist-item__image-link" aria-hidden="true" tabindex="-1">
              <img class="wishlist-item__image" src="${img}" alt="" width="80" height="80" loading="lazy">
            </a>
            <div class="wishlist-item__info">
              <a href="${url}" class="wishlist-item__title link">${title}</a>
              <div class="wishlist-item__price">${this.escapeHtml(price)}</div>
            </div>
            <div class="wishlist-item__actions">
              <button type="button" class="wishlist-add-to-cart" data-variant-id="${vid}">${this.escapeHtml(addLabel)}</button>
              <button type="button" class="wishlist-remove" data-product-id="${pid}">${this.escapeHtml(removeLabel)}</button>
            </div>
          </div>
        `;
      })
      .join('');
  }

  addToCart(variantId, triggerBtn) {
    const id = String(variantId);
    if (!id) return;

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const config =
      typeof fetchConfig === 'function'
        ? fetchConfig('javascript')
        : { method: 'POST', headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' } };

    config.headers = { ...config.headers, 'X-Requested-With': 'XMLHttpRequest' };
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', id);
    formData.append('quantity', '1');

    if (cart && typeof cart.getSectionsToRender === 'function') {
      formData.append(
        'sections',
        cart.getSectionsToRender().map((section) => section.id)
      );
      formData.append('sections_url', window.location.pathname);
      if (typeof cart.setActiveElement === 'function') cart.setActiveElement(triggerBtn || null);
    }

    config.body = formData;

    const url = window.routes?.cart_add_url || '/cart/add.js';
    if (triggerBtn) {
      triggerBtn.disabled = true;
      triggerBtn.classList.add('wishlist-add-to-cart--loading');
    }

    fetch(url, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          const msg = response.description || response.message || (window.cartStrings && window.cartStrings.error) || 'Erreur';
          window.alert(msg);
          return;
        }

        const row = triggerBtn?.closest('.wishlist-item');
        const productId = row?.dataset?.wishlistProductId;
        if (productId) this.removeFromWishlist(productId);

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'wishlist',
            productVariantId: id,
            cartData: response,
          });
        }

        if (cart && typeof cart.renderContents === 'function' && response.sections) {
          cart.renderContents(response);
        } else if (!cart) {
          window.location.href = window.routes?.cart_url || '/cart';
        }
      })
      .catch(() => {
        const err = window.cartStrings && window.cartStrings.error;
        if (err) window.alert(err);
      })
      .finally(() => {
        if (triggerBtn) {
          triggerBtn.disabled = false;
          triggerBtn.classList.remove('wishlist-add-to-cart--loading');
        }
      });
  }

  /**
   * Agrège les variantes (quantités si doublons) puis POST /cart/add.js { items: [...] }.
   */
  addAllToCart(triggerBtn) {
    const items = this.getWishlist();
    const qtyByVariant = new Map();
    for (const item of items) {
      const vid = String(item.variantId || '').trim();
      if (!vid) continue;
      qtyByVariant.set(vid, (qtyByVariant.get(vid) || 0) + 1);
    }
    const payload = [...qtyByVariant.entries()].map(([id, quantity]) => ({
      id: Number(id),
      quantity,
    }));
    if (!payload.length) return;

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const url = window.routes?.cart_add_url || '/cart/add.js';
    const baseConfig =
      typeof fetchConfig === 'function' ? fetchConfig('json') : { method: 'POST', headers: { Accept: 'application/json' } };
    const config = {
      ...baseConfig,
      headers: {
        ...baseConfig.headers,
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ items: payload }),
    };

    if (triggerBtn) {
      triggerBtn.disabled = true;
      triggerBtn.classList.add('wishlist-add-to-cart--loading');
    }

    fetch(url, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status || response.errors) {
          const msg =
            response.description ||
            response.message ||
            (typeof response.errors === 'string' ? response.errors : null) ||
            (window.cartStrings && window.cartStrings.error) ||
            'Erreur';
          window.alert(msg);
          return;
        }

        this.saveWishlist([]);
        this.updateAllHeartButtons();
        this.updateHeaderCount();
        this.renderDrawerItems();

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'wishlist',
            cartData: response,
          });
        }

        if (cart && typeof cart.renderContents === 'function' && response.sections) {
          cart.renderContents(response);
        } else if (!cart) {
          window.location.href = window.routes?.cart_url || '/cart';
        }
      })
      .catch(() => {
        const err = window.cartStrings && window.cartStrings.error;
        if (err) window.alert(err);
      })
      .finally(() => {
        if (triggerBtn) {
          triggerBtn.disabled = false;
          triggerBtn.classList.remove('wishlist-add-to-cart--loading');
        }
      });
  }

  bindEvents() {
    if (this._bound) return;
    this._bound = true;

    document.addEventListener(
      'click',
      (e) => {
        const btn = e.target.closest('.wishlist-btn');
        if (btn) {
          e.preventDefault();
          e.stopPropagation();
          this.toggleWishlistFromButton(btn);
          return;
        }

        const headerIcon = e.target.closest('.wishlist-header-icon');
        if (headerIcon) {
          e.preventDefault();
          this.openDrawer();
          return;
        }

        if (e.target.closest('.wishlist-drawer-close') || e.target.id === 'wishlist-backdrop') {
          this.closeDrawer();
          return;
        }

        const addAll = e.target.closest('.wishlist-add-all-to-cart');
        if (addAll) {
          e.preventDefault();
          this.addAllToCart(addAll);
          return;
        }

        const addCart = e.target.closest('.wishlist-add-to-cart');
        if (addCart) {
          e.preventDefault();
          const vid = addCart.dataset.variantId;
          this.addToCart(vid, addCart);
          return;
        }

        const removeBtn = e.target.closest('.wishlist-remove');
        if (removeBtn) {
          e.preventDefault();
          this.removeFromWishlist(removeBtn.dataset.productId);
        }
      },
      true
    );

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isDrawerOpen()) this.closeDrawer();
    });
  }

  init() {
    this.bindEvents();
    this.bindSubscribeVariantChange();
    this.updateAllHeartButtons();
    this.updateHeaderCount();

    document.addEventListener('shopify:section:load', () => {
      this.updateAllHeartButtons();
      this.updateHeaderCount();
      if (this.isDrawerOpen()) this.renderDrawerItems();
    });
  }
}

function bootWishlist() {
  window.mgWishlist = new WishlistManager();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootWishlist);
} else {
  bootWishlist();
}
